import type { QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";

import { ApiError, authApi } from "./api";

export const viewerQuery = { queryKey: ["auth", "me"], queryFn: authApi.me } as const;

/**
 * Route gate for signed-in pages. A 401 sends the visitor to /login with the page they
 * wanted, so the magic link brings them straight back.
 *
 * `staleTime: "static"` replaces the deprecated ensureQueryData: serve the cached viewer
 * if there is one, fetch only when there is not.
 */
export const requireSession = async (queryClient: QueryClient, redirectTo: string) => {
  try {
    return await queryClient.query({ ...viewerQuery, staleTime: "static" });
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      throw redirect({ to: "/login", search: { redirectTo } });
    }
    throw error;
  }
};

/** Whether someone is signed in, without redirecting: for pages open to both. */
export const peekSession = async (queryClient: QueryClient) => {
  try {
    return await queryClient.query({ ...viewerQuery, staleTime: "static" });
  } catch {
    return null;
  }
};

export const errorMessage = (error: unknown, fallback: string): string =>
  error instanceof ApiError ? error.message : fallback;
